/**
 * The data-table header cell: title + the trailing affordance cluster (three-dot menu +
 * sort glyph) + the resize handle. The `SortableHeaderCell` variant that wraps this as a
 * `@dnd-kit` reorder target lives in `sortable-header-cell.tsx` (a component top level is the
 * only place `useSortable` can run — hooks can't be called inside the header `.map`).
 *
 * The reorder DRAG is pointer-only: the sortable `attributes` (which set `role="button"` + a
 * tab stop) are intentionally NOT spread — they would clobber the grid's `role="columnheader"`
 * and its single-tab-stop + `aria-activedescendant` nav model. Only `listeners` (pointer drag)
 * are applied. Keyboard reorder is provided separately via the header menu's "Move left/right"
 * items (see `column-header-menu.tsx`), so reorder is still keyboard-accessible.
 */
import type { CSSProperties } from 'react'
import type { DraggableSyntheticListeners } from '@dnd-kit/core'
import { flexRender, type Header, type RowData } from '@tanstack/react-table'

import { cn } from '@gto/ui/cn'

import type { DataTableSortAffordance } from '../data-table.types'
import type { SortDirection } from './aria-sort'
import {
  ColumnHeaderMenu,
  type ColumnHeaderMenuLabels,
  type ColumnHeaderMenuMove,
} from './column-header-menu'
import { DEFAULT_COLUMN_HEADER_MENU_LABELS } from './column-header-menu.constants'
import { readColumnHeaderAlign, readGrowFactor, readSortAffordance } from './column-meta'
import { getColumnTrackStyle } from './column-size'
import { type DataTableSurface, DEFAULT_DATA_TABLE_HEADER_SURFACE } from './data-table.styles'
import { getPinnedCellProps, type PinnedOffsetMap } from './pinning.styles'
import { SortIndicator } from './sort-indicator'
import { TableHeaderCell } from './table-parts'

/**
 * The trailing-edge affordance cluster (three-dot menu + sort glyph, MUI
 * `.iconButtonContainer` parity): an IN-FLOW flex sibling of the title, width-collapsed at
 * rest and opened to its content width when revealed. Because it participates in the
 * title's flex row, flexbox shrinks the title by exactly the cluster's rendered width —
 * the `truncate` ellipsis lands at the chip's edge for ANY chip content at ANY icon size,
 * with no hand-computed clearance table and no opaque background to seam against the
 * header surface. `w-0 overflow-hidden` (not `hidden`) keeps the controls tab-reachable;
 * an unsorted column's title owns the full cell width at rest.
 */
const HEADER_AFFORDANCE_CLUSTER_CLASS = 'flex w-0 shrink-0 items-center overflow-hidden pl-0.5'

/**
 * The cluster's reveal (`w-auto`), one condition list:
 * - header hover — the pointer affordance;
 * - a VISIBLE focus within (WCAG 2.4.7): a keyboard user who tabs to the sort button or
 *   menu trigger must SEE them, not just be focused on an invisible box. `:focus-visible`
 *   (not plain focus-within) on purpose: closing the menu returns focus to its trigger,
 *   and plain focus-within kept the chip parked over the label until the next click;
 * - an open menu popup (the trigger's `ui-popup-open` marker).
 * Kept separate from the geometry so a live column resize can drop all three at once:
 * dragging a column edge sweeps the pointer across the other headers, and each one popping
 * its chip open mid-drag read as the table glitching. The SORTED column's cluster stays
 * open through `isSorted` — sort STATE, not a hover affordance.
 */
const HEADER_AFFORDANCE_REVEAL_CLASS =
  'group-hover/header:w-auto group-has-[:focus-visible]/header:w-auto has-[.ui-popup-open]:w-auto'

/**
 * The menu trigger additionally collapses WITHIN the cluster, so a sorted column at rest
 * parks only its arrow — the menu never persists (DataGridPro's `iconButtonContainer`
 * behavior). This second scope is irreducible: the arrow's visibility outlives hover
 * (while sorted), the menu's never does.
 */
// `flex items-center`, not a bare block: an inline-level IconButton inside a block wrapper
// forms a LINE BOX, whose baseline strut left the 24px trigger sitting ~1px above the
// cluster's centre line while the 16px sort glyph centred exactly — the sort icon read as
// hanging low next to it (QA round 15).
const HEADER_MENU_COLLAPSE_CLASS =
  'flex items-center w-0 overflow-hidden group-hover/header:w-auto group-has-[:focus-visible]/header:w-auto has-[.ui-popup-open]:w-auto'

/**
 * The cluster's placement for a column that sets `meta.sortAffordance`. Margins and order
 * only: the cluster sets neither, so these never fight its own geometry, and a column
 * without the meta gets no class at all.
 */
const SORT_AFFORDANCE_START_CLASS = 'order-first'
const SORT_AFFORDANCE_OUTSET_CLASS: Record<
  NonNullable<DataTableSortAffordance['side']>,
  Record<NonNullable<DataTableSortAffordance['outset']>, string>
> = {
  start: { 1.5: '-ml-1.5', 2: '-ml-2' },
  end: { 1.5: '-mr-1.5', 2: '-mr-2' },
}

const toSortAffordanceClassName = (affordance: DataTableSortAffordance | undefined) => {
  if (affordance === undefined) {
    return undefined
  }
  const side = affordance.side ?? 'end'

  return cn(
    side === 'start' && SORT_AFFORDANCE_START_CLASS,
    affordance.outset !== undefined && SORT_AFFORDANCE_OUTSET_CLASS[side][affordance.outset],
  )
}

type HeaderCellDndProps = {
  setNodeRef: (node: HTMLElement | null) => void
  listeners: DraggableSyntheticListeners
  style: CSSProperties
  isDragging: boolean
}

export type DataTableHeaderCellProps<TData extends RowData> = {
  header: Header<TData, unknown>
  /** 0-based position within the header group (drives `aria-colindex` with the selection offset). */
  headerIndex: number
  hasSelectionColumn: boolean
  pinnedOffsets: PinnedOffsetMap
  showColumnMenu: boolean
  columnMenuLabels?: Partial<ColumnHeaderMenuLabels>
  /** Keyboard reorder controls, surfaced as "Move left/right" menu items when reordering is on. */
  columnMove?: ColumnHeaderMenuMove
  /**
   * The column's sort direction, resolved by the preset from the reactive
   * `sortingState` (`resolveSortDirection`) — NOT read here via
   * `header.column.getIsSorted()`. Passing it as a prop is what keeps the sort
   * glyph / `aria-sort` live under the React Compiler, which would otherwise
   * memoize this cell on the stable `table` and freeze the indicator. See
   * `data-table.tsx`.
   */
  sortDirection: SortDirection
  /**
   * The header band's surface — the opaque background a PINNED header cell paints.
   * Optional — defaults to the DS default header surface for standalone use of this
   * exported primitive; the preset always passes the resolved header surface.
   */
  surface?: DataTableSurface
  /**
   * The column's rendered width (px), resolved by the preset from the reactive
   * `columnSizingState` (`resolveColumnWidth`) — NOT read here via `header.getSize()`.
   * Passing it as a prop is what keeps the header width live under the React Compiler,
   * which would otherwise memoize this cell on the stable `header` and freeze its width
   * mid-resize while the body cells track. Same rationale as `sortDirection`. See
   * `data-table.tsx`.
   */
  width: number
  /**
   * Whether a resize drag is live on ANY column (the preset's reactive
   * `columnSizingInfo.isResizingColumn` boolean — NOT read here off the stable `table`, same
   * React Compiler rationale as `sortDirection` / `width`). While true, every header shows
   * its LABEL only: the affordance cluster's hover / focus reveal is off band-wide, and a
   * non-dragged column's separator no longer lights up as the pointer crosses it. The
   * persistent sorted-column arrow deliberately stays — it is sort STATE, not a hover
   * affordance, and blinking it out at drag start would be the very flicker this suppresses.
   */
  isAnyColumnResizing?: boolean
  /**
   * `meta.grow` column: flexes into spare width but can also shrink within the scrollport. The
   * body takes the identical `flex` + `minWidth` branch in `data-table.tsx`, so the planes stay
   * aligned whether the grow column leads, trails, or sits between fixed columns.
   */
  grow?: boolean
  /** CSS floor for a grow column, derived from its configured `minSize`. */
  growMinWidth?: number
  /** Present only for an active reorder target (inside a `DndContext`). */
  dnd?: HeaderCellDndProps
}

/**
 * Plain generic function (not `FC`) so `Header<TData>` inference survives — same reason as
 * the menu / visibility components.
 */
export const DataTableHeaderCell = <TData extends RowData>({
  header,
  headerIndex,
  hasSelectionColumn,
  pinnedOffsets,
  showColumnMenu,
  columnMenuLabels,
  columnMove,
  sortDirection,
  surface = DEFAULT_DATA_TABLE_HEADER_SURFACE,
  width,
  isAnyColumnResizing = false,
  grow,
  growMinWidth = 0,
  dnd,
}: DataTableHeaderCellProps<TData>) => {
  const pinned = getPinnedCellProps(pinnedOffsets, header.column.id, {
    variant: 'header',
    surface,
  })
  // `width` is the preset-resolved REACTIVE size (see the prop doc) — NOT `header.getSize()`,
  // whose compiler-frozen read would leave the header behind the body during a resize. The
  // track itself is derived through the same helper the body and summary cells go through.
  const trackStyle = getColumnTrackStyle({
    grow: grow === true,
    growFactor: readGrowFactor(header.column),
    growMinWidth,
    width,
  })
  const canSort = header.column.getCanSort()
  const sortButtonLabel = columnMenuLabels?.sortLabel ?? DEFAULT_COLUMN_HEADER_MENU_LABELS.sortLabel
  const isSorted = sortDirection !== false
  const headerContent = flexRender(header.column.columnDef.header, header.getContext())

  // Sorting has its own header arrow, so a sort-only column shows no menu; the menu appears for
  // a pin action or (when reordering is on) the "Move" items. When it DOES appear it still
  // includes the Sort items — the keyboard-accessible sort path, since the header-click sort is
  // pointer-only under the grid nav model. The cluster still shows the arrow for a sortable column.
  const showHeaderMenu = showColumnMenu && (header.column.getCanPin() || columnMove !== undefined)
  const showAffordanceCluster = canSort || showHeaderMenu
  const sortAffordance = readSortAffordance(header.column)

  // `group/header` scopes the affordance cluster's hover-reveal to THIS header cell; `relative`
  // anchors the absolutely-positioned cluster + resize handle. A sortable header reads as
  // clickable wherever it is: a plain click sorts, and on a REORDER TARGET a real drag (past
  // the sensor's activation distance) still reorders. Only a non-sortable reorder target
  // advertises the drag with `cursor-grab`.
  const headerCellClassName = cn(
    'group/header relative',
    canSort ? 'cursor-pointer' : dnd !== undefined && 'cursor-grab',
    canSort && 'select-none',
    dnd?.isDragging === true && 'z-20 cursor-grabbing opacity-80',
  )
  // MUI `.columnSeparator` parity: a 1px line hidden at rest, green while resizing. It is
  // revealed by hovering the HANDLE (the reference's `&:not(:hover) { opacity: 0 }`), not the
  // whole header — keyed off `group-hover/header` it drew a full-height rule beside every
  // column the pointer merely crossed, which read as a rendering glitch rather than a grab
  // affordance. Inset vertically (`my-2`) for the same reason: a separator tick, not a border.
  //
  // The hover reveal is off entirely during a drag: the pointer crosses other columns' 8px
  // handle strips on the way, and only the column actually being resized should show a rule.
  // That column keeps its green tick through the `getIsResizing()` branch — a getter read,
  // but a safe one HERE, because `isAnyColumnResizing` re-renders this cell at both edges of
  // the drag and `width` re-renders it in between.
  const resizeHandleClassName = cn(
    'absolute inset-y-0 right-0 z-10 flex w-2 cursor-col-resize touch-none select-none justify-end',
    "after:my-2 after:w-[1px] after:bg-divider after:opacity-0 after:transition-opacity after:content-['']",
    !isAnyColumnResizing && 'hover:after:opacity-100',
    header.column.getIsResizing() && 'after:bg-primary-main after:opacity-100',
  )

  return (
    <TableHeaderCell
      ref={dnd?.setNodeRef}
      align={readColumnHeaderAlign(header.column)}
      className={headerCellClassName}
      colIndex={headerIndex + 1 + (hasSelectionColumn ? 1 : 0)}
      pinned={pinned}
      sort={sortDirection}
      // The track (inline) composes over the pinned sticky style; the reorder transform rides
      // on top.
      style={{ ...trackStyle, ...dnd?.style }}
      // Sorts on a REORDER TARGET too, with no guard against the click that ends a drag —
      // @dnd-kit already suppresses it. The moment the pointer sensor's distance constraint is
      // met it adds a `click` listener on `document` in the CAPTURE phase that stops
      // propagation; React 19 delegates from the root container, which is below `document`, so
      // that click never reaches this handler. A press-and-release never trips the constraint
      // and sorts normally. Verified in the browser, not just read: a real drag reorders and
      // leaves `aria-sort` untouched, a plain click sorts.
      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
      {...dnd?.listeners}
    >
      <span className={cn('min-w-0 truncate', sortAffordance?.hug !== true && 'flex-1')}>
        {headerContent}
      </span>
      {showAffordanceCluster && (
        <span
          className={cn(
            HEADER_AFFORDANCE_CLUSTER_CLASS,
            !isAnyColumnResizing && HEADER_AFFORDANCE_REVEAL_CLASS,
            isSorted && 'w-auto',
            toSortAffordanceClassName(sortAffordance),
          )}
        >
          {showHeaderMenu && (
            <span className={HEADER_MENU_COLLAPSE_CLASS}>
              <ColumnHeaderMenu
                column={header.column}
                labels={columnMenuLabels}
                move={columnMove}
              />
            </span>
          )}
          {canSort && (
            // A real button, always: the tab-reachable sort trigger.
            // The default label deliberately matches DataGridPro's `Sort` button, so
            // assistive tech and the e2e helpers address both engines identically; a
            // localized consumer overrides it via `columnMenuLabels.sortLabel`.
            // Pointer-down must not start a reorder drag from the sort control, and the
            // click must not bubble into the header's own sort-toggle (double-toggling
            // on non-reorderable headers).
            <button
              aria-label={sortButtonLabel}
              className="flex cursor-pointer items-center"
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                header.column.getToggleSortingHandler()?.(event)
              }}
              onPointerDown={(event) => {
                event.stopPropagation()
              }}
            >
              {/* Always painted — the collapsing cluster owns visibility, so the chevron
                  must not add its own hover gate (a keyboard-revealed chip would show an
                  empty box). Other SortIndicator consumers keep the default hover fade. */}
              <SortIndicator
                className="opacity-100"
                sort={sortDirection}
              />
            </button>
          )}
        </span>
      )}
      {header.column.getCanResize() && (
        // Drag to resize (`getResizeHandler`); double-click resets to the default width.
        // `aria-hidden` + no tabindex: a pointer-only affordance (no keyboard resize, matching the
        // reference). `stopPropagation` on pointer-down keeps a resize off the sort + reorder paths
        // (so grabbing the edge resizes instead of starting a drag).
        <span
          aria-hidden
          className={resizeHandleClassName}
          data-column-resize-handle={header.column.id}
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={() => header.column.resetSize()}
          onMouseDown={header.getResizeHandler()}
          onPointerDown={(event) => event.stopPropagation()}
          onTouchStart={header.getResizeHandler()}
        />
      )}
    </TableHeaderCell>
  )
}
